import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { tierDisplayName, type Tier } from "@/lib/tier";
import { getStripeEnvironment } from "@/lib/stripe";

type StripeEnv = "sandbox" | "live";

type OverviewPayload = {
  environment: StripeEnv;
  generated_at: string;
  signups_7d: number;
  signups_30d: number;
  total_users: number;
  total_workspaces: number;
  active_workspaces_30d: number;
  tier_distribution: Record<string, number>;
  counts_by_status: Record<string, number>;
  counts_by_price: Record<string, number>;
  price_meta: Record<string, { unit_amount: number | null; currency: string; interval: string | null; nickname: string | null; product_name: string | null }>;
  mrr_estimate_cents: number | null;
  mrr_unavailable_reason?: string | null;
  trial_to_paid_ratio: number | null;
  trials_recent: number;
  trials_converted: number;
  email_total_7d: number;
  email_bounced_7d: number;
  email_failed_7d: number;
};

const formatUsd = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);

const formatPct = (n: number | null) =>
  n == null ? "—" : `${(n * 100).toFixed(1)}%`;

export function OverviewTab() {
  const [env, setEnv] = useState<StripeEnv>(() => getStripeEnvironment());
  const [data, setData] = useState<OverviewPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = async (which: StripeEnv) => {
    setLoading(true);
    setError(null);
    const { data: payload, error: err } = await supabase.functions.invoke("admin-overview-stats", {
      body: { environment: which },
    });
    if (err || (payload && (payload as { error?: string }).error)) {
      const msg = err?.message || (payload as { error?: string })?.error || "Failed to load overview";
      setError(msg);
      // Non-blocking: don't toast on every failure — the inline pill is enough.
      setLoading(false);
      return;
    }
    setData(payload as OverviewPayload);
    setLoading(false);
  };

  useEffect(() => {
    void load(env);
  }, [env]);

  const tierRows = useMemo(() => {
    if (!data) return [] as Array<{ tier: Tier | string; count: number }>;
    const order: Array<Tier | string> = ["starter", "plus", "pro", "elite", "agency", "comp", "admin_workspace", "super_admin"];
    return order
      .filter((t) => (data.tier_distribution[t] ?? 0) > 0)
      .map((t) => ({ tier: t, count: data.tier_distribution[t] ?? 0 }));
  }, [data]);

  const bounceRate = data && data.email_total_7d > 0
    ? data.email_bounced_7d / data.email_total_7d
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Overview</h2>
          <p className="text-sm text-muted-foreground">
            Snapshot KPIs across signups, workspaces, billing, and email.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={env} onValueChange={(v) => setEnv(v as StripeEnv)}>
            <TabsList>
              <TabsTrigger value="sandbox">Sandbox</TabsTrigger>
              <TabsTrigger value="live">Live</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" onClick={() => load(env)} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <span className="font-medium">Overview unavailable:</span>
          <span className="truncate">{error}</span>
          <Button variant="outline" size="sm" className="ml-auto h-7" onClick={() => load(env)} disabled={loading}>
            Retry
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Signups (7d)" value={data ? data.signups_7d.toLocaleString() : null} loading={loading} sub={data ? `${data.signups_30d.toLocaleString()} in last 30d` : undefined} />
        <StatCard title="Total users" value={data ? data.total_users.toLocaleString() : null} loading={loading} sub={data ? `${data.total_workspaces.toLocaleString()} workspaces` : undefined} />
        <StatCard title="Active workspaces (30d)" value={data ? data.active_workspaces_30d.toLocaleString() : null} loading={loading} sub={data && data.total_workspaces > 0 ? `${((data.active_workspaces_30d / data.total_workspaces) * 100).toFixed(0)}% of total` : undefined} />
        <StatCard
          title={`MRR (${env})`}
          value={data ? (data.mrr_estimate_cents == null ? "—" : formatUsd(data.mrr_estimate_cents)) : null}
          loading={loading}
          sub={
            data
              ? data.mrr_estimate_cents == null
                ? "Stripe unavailable"
                : `${(data.counts_by_status.active ?? 0) + (data.counts_by_status.trialing ?? 0)} active+trialing`
              : undefined
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-sm">Trial → paid (last 60d)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-2xl font-semibold">{formatPct(data?.trial_to_paid_ratio ?? null)}</div>
                <div className="text-xs text-muted-foreground">
                  {data?.trials_converted ?? 0} of {data?.trials_recent ?? 0} subscriptions started in the last 60d are now active. Snapshot — no historical replay.
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Email health (7d)</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin?tab=ops")}>
              Ops <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-2xl font-semibold">{(data?.email_total_7d ?? 0).toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">
                  sends · {formatPct(bounceRate)} bounce · {data?.email_failed_7d ?? 0} failed
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Subscription status ({env})</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin?tab=billing")}>
              Billing <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(data?.counts_by_status ?? {})
                  .filter(([, c]) => c > 0)
                  .map(([status, count]) => (
                    <Badge key={status} variant="outline">
                      {status}: {count}
                    </Badge>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-sm">Tier distribution (primary workspaces)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-12 w-full" />
          ) : tierRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No workspaces yet.</p>
          ) : (
            <div className="space-y-2">
              {tierRows.map(({ tier, count }) => {
                const total = tierRows.reduce((acc, r) => acc + r.count, 0);
                const pct = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={tier} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{tierDisplayName(tier as Tier)}</span>
                      <span className="text-muted-foreground">{count} · {pct.toFixed(0)}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {data && (
        <p className="text-xs text-muted-foreground">
          Generated {new Date(data.generated_at).toLocaleString()} · environment: {env}
        </p>
      )}
    </div>
  );
}

function StatCard({ title, value, sub, loading }: { title: string; value: string | null; sub?: string; loading: boolean }) {
  return (
    <Card className="shadow-soft">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading || value == null ? (
          <Skeleton className="h-7 w-20" />
        ) : (
          <div className="text-2xl font-semibold">{value}</div>
        )}
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}
